import { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ACCEPT_CV, normaliserNumeroJoignable, type ReponseCandidatureWhatsapp } from '@rh/shared';
import { appel } from '../api';
import { controleLocalCv, envoyerCv, formaterTaille, messageEnvoiCv } from '../envoi-cv';
import { Erreur } from './Etat';

/**
 * « Candidature reçue par WhatsApp » (docs/FORMULAIRE_PUBLIC.md § 5 ter).
 *
 * Le message est arrivé sur le WhatsApp Business de l'équipe, avec la
 * référence de l'annonce : la saisie se fait ici, à la main. **La réception
 * reste manuelle dans ce lot** — l'application ne se connecte à aucun service
 * Meta et n'écrit à personne.
 *
 * Le formulaire est court à dessein : on recopie ce qu'un message WhatsApp
 * contient, rarement davantage. Le CV reçu dans la conversation se joint ici,
 * dans la foulée : il part sur la fiche une fois la candidature créée, par la
 * route de dépôt habituelle. Il reste facultatif, et un envoi qui échoue ne
 * perd pas la saisie — la fiche existe, le CV s'y dépose depuis la fiche.
 */
export function CandidatureWhatsapp({ annonceId }: { annonceId: string }) {
  const naviguer = useNavigate();
  const [ouvert, setOuvert] = useState(false);
  const [nom, setNom] = useState('');
  const [telephone, setTelephone] = useState('');
  const [ville, setVille] = useState('');
  const [memeNumero, setMemeNumero] = useState(true);
  const [whatsapp, setWhatsapp] = useState('');
  const [cv, setCv] = useState<File | null>(null);
  /** Numéro supplémentaire donné dans le message : un proche, un autre poste. */
  const [autreNumero, setAutreNumero] = useState('');
  const [envoi, setEnvoi] = useState(false);
  /** Le CV part après la candidature : un envoi de 10 Mo se dit, il peut durer. */
  const [envoiCv, setEnvoiCv] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  /** Candidature déjà créée dont le CV n'est pas parti : ne rien créer deux fois. */
  const [creee, setCreee] = useState<string | null>(null);
  const titre = useId();

  function fermer(): void {
    setOuvert(false);
    setNom('');
    setTelephone('');
    setVille('');
    setWhatsapp('');
    setMemeNumero(true);
    setCv(null);
    setAutreNumero('');
    setErreur(null);
    setCreee(null);
  }

  /** Le fichier ne part que s'il peut aboutir : vide, trop lourd ou HEIC, on le dit tout de suite. */
  function choisirCv(fichier: File | undefined): void {
    setErreur(null);
    if (!fichier) {
      setCv(null);
      return;
    }
    const refus = controleLocalCv(fichier);
    if (refus) {
      setCv(null);
      setErreur(refus);
      return;
    }
    setCv(fichier);
  }

  async function enregistrer(): Promise<void> {
    setEnvoi(true);
    setErreur(null);
    try {
      // Deuxième essai après un CV qui n'est pas parti : la candidature existe déjà.
      let candidatureId = creee;
      if (!candidatureId) {
        const bilan = await appel<ReponseCandidatureWhatsapp>(
          `/annonces/${annonceId}/candidature-whatsapp`,
          {
            method: 'POST',
            body: JSON.stringify({
              nom: nom.trim(),
              telephone: telephone.trim(),
              ...(ville.trim() ? { ville: ville.trim() } : {}),
              whatsappEstLeTelephone: memeNumero,
              ...(memeNumero || !whatsapp.trim() ? {} : { whatsapp: whatsapp.trim() }),
            }),
          },
        );
        candidatureId = bilan.candidatureId;
      }

      // Le numéro part avant le CV : c'est l'envoi court, et il n'a pas à
      // attendre qu'un fichier de 10 Mo ait fini de monter.
      if (autreNumero.trim()) {
        try {
          await appel(`/candidatures/${candidatureId}/telephones`, {
            method: 'POST',
            body: JSON.stringify({ telephone: autreNumero.trim() }),
          });
          setAutreNumero('');
        } catch (e) {
          // La candidature existe : on le dit, on ne la recrée pas, et le
          // numéro s'ajoute d'ici ou depuis la fiche.
          setCreee(candidatureId);
          setErreur(
            `Candidature enregistrée, mais le numéro n’a pas été ajouté : ${
              e instanceof Error ? e.message : 'ajout impossible'
            }`,
          );
          return;
        }
      }

      if (cv) {
        try {
          setEnvoiCv(true);
          await envoyerCv(`/candidatures/${candidatureId}/documents`, cv);
        } catch (e) {
          // La candidature est enregistrée : on ne la recrée pas, on ne la perd
          // pas non plus. Le CV se redépose d'ici ou depuis la fiche.
          setCreee(candidatureId);
          const classe = messageEnvoiCv(e);
          setErreur(
            `Candidature enregistrée, mais le CV n’est pas parti${classe ? ` : ${classe.message}` : ''}.`,
          );
          return;
        }
      }

      fermer();
      // La fiche s'ouvre : c'est là qu'on complète, qu'on relit le CV déposé et
      // qu'on note l'appel. Un doublon mène à la candidature qui existait déjà.
      naviguer(`/candidatures/${candidatureId}`);
    } catch (e) {
      setErreur(e instanceof Error ? e.message : 'Enregistrement impossible');
    } finally {
      setEnvoi(false);
      setEnvoiCv(false);
    }
  }

  return (
    <>
      <button type="button" className="bouton-second" onClick={() => setOuvert(true)}>
        Candidature reçue par WhatsApp
      </button>

      {ouvert && (
        <div className="dialogue-fond">
          <dialog open className="dialogue dialogue-whatsapp" aria-modal="true" aria-labelledby={titre}>
            <h2 id={titre}>Candidature reçue par WhatsApp</h2>
            <p className="mention">
              Recopiez ce que le message contient. Si la personne est déjà candidate sur cette
              annonce, sa candidature est simplement rouverte — il n’y en aura pas deux.
            </p>

            <label className="champ">
              <span>Nom et prénom</span>
              <input
                type="text"
                value={nom}
                maxLength={80}
                disabled={envoi}
                onChange={(e) => setNom(e.target.value)}
              />
            </label>

            <label className="champ">
              <span>Téléphone</span>
              <input
                type="tel"
                inputMode="tel"
                value={telephone}
                disabled={envoi}
                placeholder="22 123 456"
                onChange={(e) => setTelephone(e.target.value)}
              />
            </label>

            <label className="case">
              <input
                type="checkbox"
                checked={memeNumero}
                disabled={envoi}
                onChange={(e) => setMemeNumero(e.target.checked)}
              />
              <span>Le WhatsApp est le même numéro que le téléphone</span>
            </label>

            {!memeNumero && (
              <label className="champ">
                <span>Numéro WhatsApp</span>
                <input
                  type="tel"
                  inputMode="tel"
                  value={whatsapp}
                  disabled={envoi}
                  onChange={(e) => setWhatsapp(e.target.value)}
                />
              </label>
            )}

            {/* Un second numéro arrive souvent dans le même message : « rappelez
                plutôt ma sœur au … ». Il s'ajoute à la fiche comme depuis la
                fiche, et ne remplace jamais le téléphone ci-dessus. */}
            <label className="champ">
              <span>Autre numéro (facultatif)</span>
              <input
                type="tel"
                inputMode="tel"
                value={autreNumero}
                maxLength={30}
                disabled={envoi}
                onChange={(e) => setAutreNumero(e.target.value)}
              />
            </label>
            {/* Dit pourquoi le bouton ne répond pas : un bouton grisé sans
                explication se lit comme une panne. */}
            {autreNumero.trim().length > 0 && !normaliserNumeroJoignable(autreNumero) && (
              <p className="mention">
                Numéro incompréhensible : 8 chiffres, ou +216 / +33… pour un numéro étranger.
              </p>
            )}

            <label className="champ">
              <span>Ville (facultatif)</span>
              <input
                type="text"
                value={ville}
                maxLength={80}
                disabled={envoi}
                onChange={(e) => setVille(e.target.value)}
              />
            </label>

            {/* Le CV arrive dans la conversation WhatsApp : on le joint ici plutôt
                que de rouvrir la fiche. Un seul fichier, comme partout ailleurs. */}
            <label className="champ champ-cv">
              <span>CV (facultatif)</span>
              <input
                type="file"
                accept={ACCEPT_CV}
                disabled={envoi}
                onChange={(e) => {
                  choisirCv(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </label>
            {cv && (
              <p className="mention fichier-choisi">
                {cv.name} · {formaterTaille(cv.size)}{' '}
                <button type="button" className="lien" disabled={envoi} onClick={() => setCv(null)}>
                  Retirer
                </button>
              </p>
            )}

            {erreur && <Erreur message={erreur} />}

            <div className="actions">
              <button
                type="button"
                className="bouton"
                disabled={
                  envoi ||
                  nom.trim().length < 2 ||
                  telephone.trim().length < 6 ||
                  (autreNumero.trim().length > 0 && !normaliserNumeroJoignable(autreNumero))
                }
                onClick={() => void enregistrer()}
              >
                {envoi
                  ? envoiCv
                    ? 'Envoi du CV…'
                    : 'Enregistrement…'
                  : creee
                    ? 'Réessayer et ouvrir la fiche'
                    : 'Enregistrer et ouvrir la fiche'}
              </button>
              {/* La candidature existe déjà : la fenêtre ne doit pas être le seul
                  chemin vers elle, sans quoi « Annuler » la ferait disparaître. */}
              {creee && (
                <button
                  type="button"
                  className="bouton-second"
                  disabled={envoi}
                  onClick={() => {
                    const id = creee;
                    fermer();
                    naviguer(`/candidatures/${id}`);
                  }}
                >
                  Ouvrir la fiche
                </button>
              )}
              <button type="button" className="lien" disabled={envoi} onClick={fermer}>
                Annuler
              </button>
            </div>
          </dialog>
        </div>
      )}
    </>
  );
}
